import { blobToBytes } from '../../core/blob-bytes';
import type { ConvertContext, Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { imageFidelity } from '../../core/fidelity';
import { sniffFormat } from '../../core/magic-bytes';
import { LOCAL_CONVERSION_COST } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { verifyImageOutput } from '../../core/verify';
import { AVIF_MIME, resolveAvifEncoder, type AvifEncoder } from './avif-engine';
import { createBrowserCodec, sampleStepFor, type DecodedImage, type ImageCodec } from './codec';
import { prepareSvg } from './svg-rasterizer';

export const IMAGE_SOURCE_FORMATS = ['png', 'jpg', 'jpeg', 'webp', 'avif', 'svg', 'gif'];
export const IMAGE_TARGET_FORMATS = ['png', 'jpg', 'webp', 'avif'];

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  avif: 'image/avif',
  gif: 'image/gif',
  svg: 'image/svg+xml',
};

const LOSSLESS_SOURCE_FORMATS = new Set(['png']);

export interface ImageConverterDeps {
  codec?: ImageCodec;
  resolveAvif?: (codec: ImageCodec) => Promise<AvifEncoder>;
}

function throwIfAborted(context?: ConvertContext): void {
  if (context?.signal?.aborted) {
    const error = new Error('任务已取消');
    error.name = 'AbortError';
    throw error;
  }
}

export class BrowserImageConverter implements Converter {
  private readonly codec: ImageCodec;
  private readonly resolveAvif: (codec: ImageCodec) => Promise<AvifEncoder>;
  private avif: AvifEncoder | null = null;
  private avifProbe: Promise<AvifEncoder> | null = null;

  constructor(deps: ImageConverterDeps = {}) {
    this.codec = deps.codec ?? createBrowserCodec();
    this.resolveAvif = deps.resolveAvif ?? resolveAvifEncoder;
  }

  capabilities(): ConverterCapabilities {
    const targets = this.avif?.kind === 'unavailable' ? IMAGE_TARGET_FORMATS.filter((item) => item !== 'avif') : IMAGE_TARGET_FORMATS;
    return {
      id: 'image.browser.canvas',
      category: 'image',
      sourceFormats: [...IMAGE_SOURCE_FORMATS],
      targetFormats: [...targets],
      local: true,
      cloud: false,
      defaultFidelity: 'L2',
      mode: 'local',
      requiresUpload: false,
      estimatedCost: LOCAL_CONVERSION_COST,
      available: this.avif?.kind !== 'unavailable',
      unavailableReason: this.avif?.kind === 'unavailable' ? '浏览器不支持 AVIF 编码，WASM 兜底引擎也不可用' : undefined,
    };
  }

  /** 异步探测 AVIF 编码能力，能力列表据此增删 avif 目标格式。 */
  async probe(): Promise<ConverterCapabilities> {
    await this.ensureAvif();
    return this.capabilities();
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const capability = this.capabilities();
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return capability.sourceFormats.includes(sourceExtension) && capability.targetFormats.includes(target);
  }

  async inspect(file: File): Promise<FileMetadata> {
    const bytes = await blobToBytes(file);
    const detected = sniffFormat(bytes);
    const sourceExtension = detected?.extension ?? extensionOf(file.name);
    const decoded = await this.decodeSource(bytes, sourceExtension, file);
    try {
      return {
        name: file.name,
        size: file.size,
        type: { extension: sourceExtension, mime: detected?.mime ?? file.type ?? null, category: 'image' },
        width: decoded.width,
        height: decoded.height,
        hasAlpha: decoded.hasAlpha,
      };
    } finally {
      decoded.close();
    }
  }

  async convert(file: File, options: ConvertOptions = {}, context?: ConvertContext): Promise<ConversionResult> {
    const target = canonicalExtension(String(options.targetFormat ?? mimeToExtension(options.targetMime) ?? 'webp'));
    if (!this.capabilities().targetFormats.includes(target)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `当前引擎不支持输出 ${target} 格式`);
    }
    const targetMime = MIME_BY_EXTENSION[target] ?? 'image/png';

    throwIfAborted(context);
    context?.onProgress?.({ progress: 5, stage: '读取文件' });
    const sourceBytes = await blobToBytes(file);
    const sourceExtension = sniffFormat(sourceBytes)?.extension ?? extensionOf(file.name);

    context?.onProgress?.({ progress: 15, stage: '解码' });
    const decoded = await this.decodeSource(sourceBytes, sourceExtension, file);
    let working: DecodedImage = decoded;
    let verification: { passed: boolean; details: string[] } = { passed: false, details: [] };

    try {
      const targetWidth = Math.max(1, Math.round(options.width ?? decoded.width));
      const targetHeight = Math.max(1, Math.round(options.height ?? decoded.height));
      if (targetWidth !== decoded.width || targetHeight !== decoded.height) {
        throwIfAborted(context);
        working = await decoded.resize(targetWidth, targetHeight);
      }

      throwIfAborted(context);
      context?.onProgress?.({ progress: 45, stage: '编码' });
      const pixels = await working.readPixels();
      const quality = typeof options.quality === 'number' ? options.quality : 0.92;
      const outputBytes = await this.encodeOutput(pixels, working.width, working.height, target, targetMime, quality);

      throwIfAborted(context);
      context?.onProgress?.({ progress: 75, stage: '校验' });
      const losslessEncode = target === 'png';
      const expectLosslessPixels = losslessEncode && LOSSLESS_SOURCE_FORMATS.has(sourceExtension);
      const step = sampleStepFor(working.width, working.height);
      const sourceSample = expectLosslessPixels ? await working.samplePixels(step) : null;

      const verified = await this.codec.decode(outputBytes, targetMime);
      const outputSample = expectLosslessPixels ? await verified.samplePixels(step) : null;
      verification = verifyImageOutput({
        expectedWidth: working.width,
        expectedHeight: working.height,
        outputWidth: verified.width,
        outputHeight: verified.height,
        expectLosslessPixels,
        sourceSample,
        outputSample,
      });
      verified.close();

      const fidelity = imageFidelity({
        sourceExtension,
        targetExtension: target,
        losslessEncode,
        hasAlpha: decoded.hasAlpha,
        multiFrame: sourceExtension === 'gif',
        width: working.width,
        height: working.height,
      });

      return {
        blob: new Blob([outputBytes as unknown as ArrayBuffer], { type: targetMime }),
        fileName: buildOutputName(file.name, target),
        metadata: {
          name: buildOutputName(file.name, target),
          size: outputBytes.length,
          type: { extension: target, mime: targetMime, category: 'image' },
          width: working.width,
          height: working.height,
          hasAlpha: target !== 'jpg' && decoded.hasAlpha,
        },
        fidelity,
        verificationPassed: verification.passed,
        verificationDetails: verification.details,
      };
    } finally {
      if (working !== decoded) working.close();
      decoded.close();
    }
  }

  private async decodeSource(bytes: Uint8Array, sourceExtension: string, file: File): Promise<DecodedImage> {
    if (sourceExtension === 'svg' || file.type === 'image/svg+xml') {
      const prepared = prepareSvg(new TextDecoder('utf-8').decode(bytes));
      return this.codec.decode(prepared.bytes, 'image/svg+xml');
    }
    const mime = MIME_BY_EXTENSION[sourceExtension] ?? file.type ?? 'application/octet-stream';
    const decoded = await this.codec.decode(bytes, mime);
    if (decoded.width <= 0 || decoded.height <= 0) {
      decoded.close();
      throw new EngineFailure('DECODE_FAILED', '图片尺寸无效，文件可能已损坏');
    }
    return decoded;
  }

  private async encodeOutput(
    pixels: Uint8ClampedArray,
    width: number,
    height: number,
    target: string,
    targetMime: string,
    quality: number,
  ): Promise<Uint8Array> {
    if (target !== 'avif') return this.codec.encode(pixels, width, height, targetMime, quality);

    const avif = await this.ensureAvif();
    if (avif.kind === 'unavailable') {
      throw new EngineFailure(
        'ENGINE_UNAVAILABLE',
        '当前浏览器不支持 AVIF 编码，且 WASM 兜底引擎不可用；请改用 WebP 或 PNG。',
      );
    }
    if (avif.kind === 'native') return this.codec.encode(pixels, width, height, AVIF_MIME, quality);
    if (!avif.encodeViaWasm) throw new EngineFailure('ENGINE_UNAVAILABLE', 'AVIF WASM 编码器加载失败');
    return avif.encodeViaWasm(pixels, width, height, Math.max(1, Math.min(100, Math.round(quality * 100))));
  }

  private ensureAvif(): Promise<AvifEncoder> {
    if (!this.avifProbe) {
      this.avifProbe = this.resolveAvif(this.codec).then((encoder) => {
        this.avif = encoder;
        return encoder;
      });
    }
    return this.avifProbe;
  }
}

function mimeToExtension(mime: unknown): string | null {
  if (typeof mime !== 'string' || !mime) return null;
  const normalized = mime.split(';')[0].trim().toLowerCase();
  const entry = Object.entries(MIME_BY_EXTENSION).find(([, value]) => value === normalized);
  return entry ? entry[0] : null;
}
